import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Box, FormControl, Grid, InputAdornment, TextareaAutosize, TextField } from '@material-ui/core';
import { btMediumGrayColor } from '../../../assets/jss/nextjs-material-kit-pro';

const styles = makeStyles((theme) => ({
  sectionSpacer: {
    height: 50,
    padding: '10px 0',
  },
  rowSpacer: {
    height: 30,
  },
  textRowSpacer: {
    [theme.breakpoints.up('sm')]: {
      height: 50,
    },
  },
  textRowLabelSpacer: {
    [theme.breakpoints.down('md')]: {
      height: 30,
      paddingTop: 5,
    },
    [theme.breakpoints.up('md')]: {
      height: 50,
      paddingTop: 8,
    },
  },
  textArea5RowSpacer: {
    [theme.breakpoints.down('sm')]: {
      height: 115,
    },
    [theme.breakpoints.up('sm')]: {
      height: 105,
    },
  },
  [theme.breakpoints.down('xs')]: {
    textField: {
      fontSize: '0.75rem',
    },
    root: {
      '& .MuiFormLabel-root': {
        fontSize: '0.75rem',
      },
    },
  },
  radioLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    '& svg': {
      width: '1.25rem',
      height: '1.25rem',
    },
  },
  textRowLargeLabelSpacer: {
    [theme.breakpoints.down('sm')]: {
      height: 60,
      paddingTop: 5,
    },
    [theme.breakpoints.up('sm')]: {
      height: 50,
      paddingTop: 8,
    },
  },
  textAreaFont: {
    fontSize: '0.938rem',
    fontFamily: 'Source Sans Pro, Helvetica, Roboto, Arial, sans-serif',
    padding: 10,
  },
  textAreaFixed: {
    resize: 'none',
    width: '100%',
    height: '105px',
    minHeight: '105px',
    maxHeight: '105px',
  },
  printedContent: {
    '@media print': {
      marginBottom: 10,
    },
    '@media screen': {
      display: 'none',
    },
  },
  unprintedContent: {
    '@media print': {
      display: 'none',
    },
    '@media screen': {},
  },
  alignPrintedContent: {
    lineHeight: '25px',
    verticalAlign: 'top',
  },
  textSpacer: {
    margin: '15px 0 5px 0',
  },
  textFieldBackground: {
    backgroundColor: 'white',
  },
  bold: {
    fontWeight: 600,
  },
  subtleBold: {
    fontWeight: 500,
    color: btMediumGrayColor,
  },
  subtleBoldLabel: {
    fontWeight: 500,
    fontSize: '0.95rem',
  },
  subTitle: {
    margin: '15px 0',
    fontSize: '1.05rem',
    fontWeight: 500,
  },
  firstSectionSpacer: {
    height: 40,
  },
  sectionTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.00rem',
      fontWeight: 600,
      margin: '8px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.10rem',
      fontWeight: 600,
      margin: '15px 0',
    },
  },
  reminder: {
    color: 'blue',
    fontSize: '1.05rem',
    margin: '10px 0',
  },
  sectionSubTitle: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.80rem',
      fontWeight: 600,
      margin: '2px 0',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '0.90rem',
      fontWeight: 600,
      margin: '5px 0',
    },
  },
}));

export default function HousingPlanV2(props) {
  const { form, setForm, disabled, housingPlanText, label } = props;

  const classes = styles();

  return (
    <Box sx={{ width: '90%' }}>
      <Grid container direction="row" className={classes.sectionSpacer} style={{ marginTop: '10px' }}>
        <Grid item xs={12} align="left">
          <section align="left" className={classes.sectionSubTitle}>
            Housing Plan
          </section>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.unprintedContent}>
        <Grid item xs={false} md={1} lg={1} />
        <Grid item xs={12} md={5} lg={3} className={classes.textRowLabelSpacer}>
          <label>{housingPlanText}</label>
        </Grid>
        <Grid item xs={12} md={6} lg={8} className={classes.textRowSpacer}>
          <FormControl fullWidth>
            <TextField
              id="housing-plan"
              size="small"
              hiddenLabel
              variant="outlined"
              value={form.summerHousingPlan}
              onChange={(event) => setForm({ ...form, summerHousingPlan: event.target.value })}
              disabled={disabled}
              InputProps={{ classes: { input: classes.textField } }}
              inputProps={{ maxLength: 250 }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <div className={classes.printedContent}>
        <div>
          <label className={classes.alignPrintedContent}>{housingPlanText}</label>
        </div>
        <div>
          <summary className={classes.alignPrintedContent}>{form.summerHousingPlan || null}</summary>
        </div>
      </div>

      <Grid container direction="row" className={classes.unprintedContent}>
        <Grid item xs={false} md={1} lg={1} />
        <Grid item xs={12} md={5} lg={3} className={classes.textRowLabelSpacer}>
          <label>What is the cost per month of this housing?</label>
        </Grid>
        <Grid item xs={6} md={2} lg={3} className={classes.textRowSpacer}>
          <FormControl fullWidth>
            <TextField
              id="summer-housing-plan-cost"
              type="number"
              size="small"
              hiddenLabel
              variant="outlined"
              value={form.summerHousingPlanCost}
              onChange={(event) => setForm({ ...form, summerHousingPlanCost: event.target.value })}
              disabled={disabled}
              InputProps={{
                maxLength: 5,
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                classes: { input: classes.textField },
              }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container direction="row" className={classes.printedContent}>
        <Grid item xs={4}>
          <label className={classes.alignPrintedContent}>Cost per month of this housing</label>
        </Grid>
        <Grid item xs={8}>
          <summary className={classes.alignPrintedContent}>$ {form.summerHousingPlanCost || null}</summary>
        </Grid>
      </Grid>

      <Grid container direction="row">
        <Grid item xs={false} md={1} lg={1} />
        <Grid item xs={12} md={5} lg={3} className={classes.rowSpacer}>
          <label>{label}</label>
        </Grid>
        <Grid item xs={12} md={6} lg={8} className={classes.textArea5RowSpacer + ' ' + classes.unprintedContent}>
          <FormControl fullWidth>
            <TextareaAutosize
              aria-label="summer-housing-expectations"
              minRows={5}
              maxRows={5}
              maxLength={750}
              value={form.summerHousingExpectations}
              onChange={(event) => setForm({ ...form, summerHousingExpectations: event.target.value })}
              disabled={disabled}
              className={`${classes.textAreaFont} ${classes.textAreaFixed}`}
              style={{
                resize: 'none',
                width: '100%',
                height: '105px',
                minHeight: '105px',
                maxHeight: '105px',
                color: disabled && 'rgba(0, 0, 0, 0.4)',
              }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <p className={classes.printedContent} style={{ whiteSpace: 'pre-wrap' }}>
        {form.summerHousingExpectations || null}
      </p>
    </Box>
  );
}

HousingPlanV2.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  disabled: PropTypes.bool,
};
